import { Link } from "@mui/material";
import Box from "@mui/material/Box";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import chroma from "chroma-js";
import React from "react";

const scale = chroma.scale([
  "rgba(0,0,255,0.5)",
  "#C2C2C2",
  "rgba(255,0,0,0.5)",
]);

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#073060ff", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#61b8ffff", "#FFFFFF"],
  DET: ["#0C2C56", "#f62e23ff", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#143680ff", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#be8e14ff", "#FFFFFF"],
  STL: ["#BE0A15", "#b6b31bff", "#FFFFFF"],
  LAD: ["#005A9D", "#005184ff", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#000000ff", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  "-": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export const hitter_columns: GridColDef<any>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Link href={"/st/player/" + params.row.id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 && value !== '-' ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 && value !== '-' ? value : "FA";
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = teamColors[params.value][0]

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[params.value][2],
            textShadow: "-1px -1px 0 " + teamColors[params.value][1] + ", 1px -1px 0 " + teamColors[params.value][1] + ", -1px 1px 0 " + teamColors[params.value][1] + ", 1px 1px 0 " + teamColors[params.value][1] + "",
          }}
        >
          {(params.value)}
        </Box>
      );
    },
  },
  { field: "Position", headerName: "Pos", width: 80 },
  { field: "age", headerName: "Age", width: 60 },
  { field: "pa", headerName: "PA", width: 60 },
  { field: "ab", headerName: "AB", width: 60 },
  { field: "pitches", headerName: "Pitches", width: 70 },
  { field: "percent_tracked", headerName: "Tracked%", width: 90 },
  {
    field: "woba",
    headerName: "wOBA",
    width: 70,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "bat_speed",
    headerName: "Bat Speed",
    width: 90,
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = params.row.bat_speed_p === 0 ? "grey" : scale(params.row.bat_speed_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value === 0 ? '-' : params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "barrelbbe",
    headerName: "Barrel %",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.barrelbbe_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "bbrate",
    headerName: "BB%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.bbrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "chaserate",
    headerName: "Chase%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.chaserate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev",
    headerName: "EV",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.ev_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "maxev",
    headerName: "Max EV",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.maxev_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev50",
    headerName: "50th% EV",
    width: 100,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.ev50_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev90",
    headerName: "90th% EV",
    width: 100,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.ev90_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "hhrate",
    headerName: "Hard Hit%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.hhrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "krate",
    headerName: "K%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.krate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "langle",
    headerName: "L°",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.langle_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "whiffrate",
    headerName: "Whiff%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.whiffrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swing",
    headerName: "Swing%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.swing_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zswing",
    headerName: "Z-Swing%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.zswing_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zcontact",
    headerName: "Z-Contact%",
    width: 110,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.zcontact_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swstr",
    headerName: "SwStr%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.swstr_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "pullair",
    headerName: "Pull Air%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.pullair_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ba",
    headerName: "BA",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "obp",
    headerName: "OBP",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "slg",
    headerName: "SLG",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "babip",
    headerName: "BABIP",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  { field: "bb", headerName: "BB", width: 90 },
  { field: "doubles", headerName: "2B", width: 90 },
  { field: "triples", headerName: "3B", width: 90 },
  { field: "hits", headerName: "Hits", width: 90 },
  { field: "hr", headerName: "HR", width: 90 },
  { field: "k", headerName: "K", width: 90 },
  { field: "swings", headerName: "Swings", width: 90 },
  { field: "whiffs", headerName: "Whiffs", width: 90 },
];

export const pitcher_columns: GridColDef<any>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Link href={"/st/player/" + params.row.id} underline="hover">
            {params.value}
          </Link>
        </Box>
      );
    },
  },
  {
    field: "MLB_AbbName",
    headerName: "Org",
    width: 60,
    valueGetter: (value: any) => {
      return value !== "0" && value !== 0 && value !== '-' ? value.toString() : "FA";
    },
    valueFormatter: (value: any) => {
      return value !== "0" && value !== 0 && value !== '-' ? value : "FA";
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = teamColors[params.value][0]

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
            fontWeight: "bold",
            color: teamColors[params.value][2],
            textShadow: "-1px -1px 0 " + teamColors[params.value][1] + ", 1px -1px 0 " + teamColors[params.value][1] + ", -1px 1px 0 " + teamColors[params.value][1] + ", 1px 1px 0 " + teamColors[params.value][1] + "",
          }}
        >
          {(params.value)}
        </Box>
      );
    },
  },
  { field: "Position", headerName: "Pos", width: 60 },
  { field: "age", headerName: "Age", width: 60 },
  { field: "pa", headerName: "PA", width: 60 },
  { field: "ab", headerName: "AB", width: 60 },
  { field: "pitches", headerName: "Pitches", width: 70 },
  { field: "percent_tracked", headerName: "Tracked%", width: 90 },
  {
    field: "woba",
    headerName: "wOBA",
    width: 70,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "weighted_psStuff",
    headerName: "psStuff+",
    width: 80,
    valueGetter: (value: any) => {
      return value === '-' ? 0 : value;
    },
    valueFormatter: (value: number) => {
      return value === undefined ? 0 : value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.weighted_psStuff_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value === undefined ? 0 : params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "max_velo",
    headerName: "Max Velo",
    width: 80,
    valueGetter: (value: any) => {
      return value === '-' ? 0 : value;
    },
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.velo_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "barrelbbe",
    headerName: "Barrel%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.barrelbbe_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "bbrate",
    headerName: "BB%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.bbrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "chaserate",
    headerName: "Chase%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.chaserate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ev",
    headerName: "EV",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.ev_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "hhrate",
    headerName: "Hard Hit%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.hhrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "krate",
    headerName: "K%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.krate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "langle",
    headerName: "L°",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.langle_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "whiffrate",
    headerName: "Whiff%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.whiffrate_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swing",
    headerName: "Swing%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.swing_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zswing",
    headerName: "Z-Swing%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.zswing_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "zcontact",
    headerName: "Z-Contact%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.zcontact_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "swstr",
    headerName: "SwStr%",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(1);
    },
    cellClassName: (params: GridCellParams<any>) => {
      return "super-app";
    },
    renderCell: (params: GridRenderCellParams) => {
      const color = scale(params.row.swstr_p).toString();

      return (
        <Box
          sx={{
            backgroundColor: color,
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          {params.value.toFixed(1)}
        </Box>
      );
    },
  },
  {
    field: "ba",
    headerName: "BA",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "obp",
    headerName: "OBP",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "slg",
    headerName: "SLG",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  {
    field: "babip",
    headerName: "BABIP",
    width: 90,
    valueFormatter: (value: number) => {
      return value.toFixed(3).toString().substring(1);
    },
  },
  { field: "bb", headerName: "BB", width: 90 },
  { field: "doubles", headerName: "2B", width: 90 },
  { field: "triples", headerName: "3B", width: 90 },
  { field: "hits", headerName: "Hits", width: 90 },
  { field: "hr", headerName: "HR", width: 90 },
  { field: "k", headerName: "K", width: 90 },
  { field: "swings", headerName: "Swings", width: 90 },
  { field: "whiffs", headerName: "Whiffs", width: 90 },
];

const mobile_width = 25;

export const hitter_columns_mobile: GridColDef<any>[] = hitter_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));

export const pitcher_columns_mobile: GridColDef<any>[] = pitcher_columns.map((col) => ({
  ...col,
  width: col.width ? col.width + mobile_width : col.width,
}));
